import React, {useState, useEffect, useCallback, useRef} from 'react'
import {createPortal} from 'react-dom'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import {toast} from 'react-toastify'
import {useAuthClient} from '../utils/use-auth-client'

// ── Reuse ProposalTool design system ────────────────────────────────────────
import {theme} from '../pages/ProposalTool/features/Dashboard/theme'
import {
  ContentArea,
  MainArea,
  ModalOverlay,
  ModalBox,
  ModalActions,
  ModalCancelBtn,
  FormInput,
  FormSelect,
  FormLabel,
  FormField,
} from '../pages/ProposalTool/features/Dashboard/styled.components'
import {
  TableWrapper,
  StyledTable,
  THead,
  TBody,
  TR,
  TH,
  TD,
  ActionBtn,
  EmptyState,
  EmptyStateTitle,
} from '../pages/ProposalTool/components/ProposalTable/styled.components'
import {
  DateRangeWrapper,
  DateRangeTrigger,
  CalendarPopout,
} from '../pages/ProposalTool/components/ProposalFilters/styled.components'
import {ProposalPagination} from '../pages/ProposalTool/components/ProposalPagination'
import {PTView, PTCalender, PTCaretDown} from '../assets'

dayjs.extend(utc)
dayjs.extend(timezone)

const DEFAULT_TIMEZONE = 'America/Chicago'

const formatDate = dateStr =>
  dateStr
    ? dayjs(dateStr).tz(DEFAULT_TIMEZONE).format('MM/DD/YYYY hh:mmA z')
    : 'N/A'

const useDebouncedValue = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

// ── Status badge colors ─────────

const STATUS_CONFIG = {
  Completed: {bg: '#E8F5E9', color: '#2E7D32', label: 'Completed'},
  Failed: {bg: '#fdecea', color: '#c62828', label: 'Failed'},
  Submitted: {bg: '#F4F6FB', color: '#0327A9', label: 'Not Started'},
  NotStarted: {bg: '#F4F6FB', color: '#0327A9', label: 'Not Started'},
}

// ── Minimal new styled components (only what can't be reused as-is) ──────────

const PageTitle = styled.h3`
  margin: 0 0 4px;
  font-size: clamp(15px, 1.5vw, 20px);
  font-weight: 700;
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
`

const ResultCount = styled.p`
  margin: 0 0 20px;
  font-size: clamp(11px, 1vw, 13px);
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
`

const FiltersRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 12px;
`

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: ${theme.radius.full};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  background: ${props =>
    (STATUS_CONFIG[props.status] || STATUS_CONFIG.Failed).bg};
  color: ${props =>
    (STATUS_CONFIG[props.status] || STATUS_CONFIG.Failed).color};
`

// Detail modal uses a wider box than the default ModalBox (400px)
// overflow: hidden preserves border-radius when content scrolls inside
const DetailModalBox = styled(ModalBox)`
  max-width: 780px;
  width: 94%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 0;
  background: #fff;
`

const ModalScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 24px;
  scrollbar-gutter: stable;
  min-height: 220px;
`

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 32px;
    top: 31px;
    bottom: 31px;
    width: 2px;
    background: ${theme.colors.border};
    z-index: 0;
  }
`

const StepCard = styled.div`
  display: flex;
  align-items: ${props => (props.failed ? 'flex-start' : 'center')};
  gap: 14px;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid ${props => (props.failed ? '#f5c6c6' : theme.colors.border)};
  border-radius: ${theme.radius.lg};
  transition: box-shadow ${theme.transition.fast};
  position: relative;
  z-index: 1;
`

const StepBadge = styled.div`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  font-family: ${theme.font.family};
  background: ${props => props.bg};
  color: ${props => props.color};
  position: relative;
  z-index: 1;
`

const StepBody = styled.div`
  flex: 1;
  min-width: 0;
`

const StepDates = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
  font-family: ${theme.font.family};
  font-size: 11px;
  color: ${theme.colors.muted};
`

const StepRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const StepName = styled.span`
  font-family: ${theme.font.family};
  font-weight: 600;
  font-size: 13px;
  color: ${theme.colors.text};
`

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${theme.colors.border};
  border-top-color: #0327a9;
  border-radius: 50%;
  animation: txn-spin 0.7s linear infinite;

  @keyframes txn-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const FailureBox = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #fdecea;
  border-radius: ${theme.radius.sm};
  font-family: ${theme.font.family};
  font-size: 12px;
  color: #c62828;
  line-height: 1.4;
  max-height: 160px;
  overflow: auto;
  word-break: break-all;
  white-space: pre-wrap;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.border};
`

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
`

const ModalSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
`

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: ${theme.colors.muted};
  line-height: 1;
  padding: 2px 6px;
  border-radius: ${theme.radius.sm};
  transition: background ${theme.transition.fast};

  &:hover {
    background: ${theme.colors.hover};
    color: ${theme.colors.text};
  }

  &:focus {
    outline: none;
  }
`

const ClearBtn = styled(ModalCancelBtn)`
  white-space: nowrap;
  background: #f4f6fb;
  border-color: #0327a9;
  color: #0327a9;

  &:hover {
    background: #e8ecf9;
  }
`

// ── Date Range Picker ────────────────────────────────────────────────────────

const DateRangePicker = ({startDate, endDate, onChange, onClear}) => {
  const [open, setOpen] = useState(false)
  const [selecting, setSelecting] = useState('start')
  const ref = useRef(null)

  useEffect(() => {
    const handler = e => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleDayClick = date => {
    if (selecting === 'start') {
      onChange({startDate: date, endDate: null})
      setSelecting('end')
    } else {
      onChange({
        startDate: dayjs(date).isBefore(dayjs(startDate)) ? date : startDate,
        endDate: dayjs(date).isBefore(dayjs(startDate)) ? startDate : date,
      })
      setSelecting('start')
      setOpen(false)
    }
  }

  const hasValue = !!(startDate || endDate)
  const label = hasValue
    ? startDate && endDate
      ? `${dayjs(startDate).format('MM/DD/YY')} — ${dayjs(endDate).format(
          'MM/DD/YY',
        )}`
      : `From ${dayjs(startDate).format('MM/DD/YY')}`
    : 'MM/DD/YY — MM/DD/YY'

  return (
    <FormField
      style={{flex: '1 1 200px', minWidth: '180px', position: 'relative'}}
    >
      <FormLabel>Date Range</FormLabel>
      <DateRangeWrapper ref={ref} style={{width: '100%'}}>
        <DateRangeTrigger
          type="button"
          hasValue={hasValue}
          onClick={() => {
            setOpen(o => !o)
            setSelecting('start')
          }}
        >
          <img
            src={PTCalender}
            width="13"
            alt=""
            style={{filter: 'brightness(0) invert(0.5)', flexShrink: 0}}
          />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
          {hasValue ? (
            <span
              role="button"
              onClick={e => {
                e.stopPropagation()
                onClear()
                setOpen(false)
              }}
              style={{fontSize: '16px', lineHeight: 1, cursor: 'pointer'}}
            >
              ×
            </span>
          ) : (
            <img
              src={PTCaretDown}
              width="10"
              alt=""
              style={{
                filter: 'brightness(0) invert(0.5)',
                flexShrink: 0,
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s ease',
              }}
            />
          )}
        </DateRangeTrigger>

        {open && (
          <CalendarPopout style={{left: 0, right: 'auto'}}>
            <p
              style={{
                margin: '8px 12px 4px',
                fontFamily: theme.font.family,
                fontSize: '12px',
                color: theme.colors.muted,
              }}
            >
              {selecting === 'start' ? 'Select start date' : 'Select end date'}
            </p>
            <Calendar
              onClickDay={handleDayClick}
              value={startDate || new Date()}
            />
          </CalendarPopout>
        )}
      </DateRangeWrapper>
    </FormField>
  )
}

// ── Detail Modal ─────────────────────────────────────────────────────────────

const TxnStatusPill = ({status}) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Failed
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: 500,
        background: cfg.bg,
        color: cfg.color,
        fontFamily: theme.font.family,
      }}
    >
      {cfg.label}
    </span>
  )
}

const DetailModal = ({transactionId, steps, loading, onClose}) => {
  if (!transactionId) return null

  const hasFailed = steps.some(s => s.status === 'Failed')

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <DetailModalBox onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <ModalTitle>Transaction Details</ModalTitle>
            <ModalSubtitle>Order ID: {transactionId}</ModalSubtitle>
          </div>
          <CloseBtn onClick={onClose}>&times;</CloseBtn>
        </ModalHeader>

        {hasFailed && (
          <div
            style={{
              background: '#fdecea',
              border: '1px solid #f5c6c6',
              borderRadius: theme.radius.md,
              padding: '10px 14px',
              marginBottom: '16px',
              fontFamily: theme.font.family,
              fontSize: '13px',
              color: '#c62828',
              fontWeight: 600,
            }}
          >
            One or more steps in this transaction failed. Review error details
            below.
          </div>
        )}

        <ModalScrollContent>
          {loading ? (
            <SpinnerWrap>
              <Spinner />
            </SpinnerWrap>
          ) : steps.length === 0 ? (
            <p
              style={{fontFamily: theme.font.family, color: theme.colors.muted}}
            >
              No steps found for this transaction.
            </p>
          ) : (
            <StepList>
              {steps.map(step => {
                const cfg = STATUS_CONFIG[step.status] || STATUS_CONFIG.Failed
                return (
                  <StepCard key={step.id} failed={step.status === 'Failed'}>
                    <StepBadge bg={cfg.bg} color={cfg.color}>
                      {step.step_number}
                    </StepBadge>
                    <StepBody>
                      <StepRow>
                        <StepName>{step.step_name}</StepName>
                        <TxnStatusPill status={step.status} />
                      </StepRow>
                      <StepDates>
                        <span>Created: {formatDate(step.created_at)}</span>
                        {step.step_completed_at && (
                          <span>
                            Completed: {formatDate(step.step_completed_at)}
                          </span>
                        )}
                      </StepDates>
                      {step.status === 'Failed' && (
                        <FailureBox>
                          <strong>Failure reason:</strong>{' '}
                          {step.error_description ||
                            'No description available.'}
                        </FailureBox>
                      )}
                    </StepBody>
                  </StepCard>
                )
              })}
            </StepList>
          )}
        </ModalScrollContent>

        <ModalActions
          style={{
            padding: '16px 0 24px',
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          <ModalCancelBtn type="button" onClick={onClose}>
            Close
          </ModalCancelBtn>
        </ModalActions>
      </DetailModalBox>
    </ModalOverlay>,
    document.body,
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

const ResizeHandle = styled.div`
  position: absolute;
  right: -4px;
  top: 15%;
  bottom: 15%;
  width: 10px;
  cursor: col-resize;
  user-select: none;
  z-index: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
  &::after {
    content: '';
    width: 2px;
    border-radius: 1px;
    background: rgba(0, 0, 0, 0.12);
    transition: background 0.15s ease;
  }
  &:hover::after {
    background: #0327a9;
  }
`

const COLUMNS = [
  {key: 'recurly_account_code', label: 'Recurly Account Code'},
  {key: 'account_email', label: 'Account Email'},
  {key: 'account_name', label: 'Account Name'},
  {key: 'status', label: 'Status'},
  {key: 'created_at', label: 'Submitted Date'},
  {key: 'actions', label: ''},
]

const INIT_WIDTHS = [200, 180, 180, 110, 150, 50]

export function PurchaseLinksDashboard() {
  const client = useAuthClient()

  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)

  const [statusFilter, setStatusFilter] = useState('')
  const [orderIdFilter, setOrderIdFilter] = useState('')
  const [recurlyFilter, setRecurlyFilter] = useState('')
  const [emailFilter, setEmailFilter] = useState('')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [sortBy, setSortBy] = useState('')
  const [sortDir, setSortDir] = useState('desc')

  const [selectedOrderId, setSelectedOrderId] = useState(null)
  const [detailSteps, setDetailSteps] = useState([])
  const [detailLoading, setDetailLoading] = useState(false)

  const [colWidths, setColWidths] = useState(INIT_WIDTHS)
  const resizingRef = useRef(null)
  const tableRef = useRef(null)

  const autoFitColumn = useCallback(colIndex => {
    if (!tableRef.current) return
    const cells = tableRef.current.querySelectorAll(
      `td:nth-child(${colIndex + 1}), th:nth-child(${colIndex + 1})`,
    )
    let maxWidth = 0
    cells.forEach(cell => {
      if (cell.scrollWidth > maxWidth) maxWidth = cell.scrollWidth
    })
    if (maxWidth > 0) {
      setColWidths(prev => {
        const next = [...prev]
        next[colIndex] = maxWidth
        return next
      })
    }
  }, [])

  const startResize = useCallback(
    (e, colIndex) => {
      e.preventDefault()
      resizingRef.current = {
        colIndex,
        startX: e.clientX,
        startWidth: colWidths[colIndex],
      }
      const onMouseMove = ev => {
        if (!resizingRef.current) return
        const {colIndex: ci, startX, startWidth} = resizingRef.current
        const newWidth = Math.max(40, startWidth + (ev.clientX - startX))
        setColWidths(prev => {
          const next = [...prev]
          next[ci] = newWidth
          return next
        })
      }
      const onMouseUp = () => {
        resizingRef.current = null
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [colWidths],
  )

  const debouncedOrderId = useDebouncedValue(orderIdFilter)
  const debouncedRecurly = useDebouncedValue(recurlyFilter)
  const debouncedEmail = useDebouncedValue(emailFilter)

  const handleSort = key => {
    if (sortBy === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('page', page)
      params.append('pageSize', perPage)
      if (statusFilter) params.append('status', statusFilter)
      if (debouncedOrderId) params.append('orderId', debouncedOrderId)
      if (debouncedRecurly)
        params.append('recurlyAccountCode', debouncedRecurly)
      if (debouncedEmail) params.append('email', debouncedEmail)
      if (startDate)
        params.append('startDate', dayjs(startDate).format('YYYY-MM-DD'))
      if (endDate)
        params.append(
          'endDate',
          dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        )
      if (sortBy) params.append('sortBy', sortBy)
      if (sortBy) params.append('sortDir', sortDir)

      const result = await client(`purchase-txn?${params.toString()}`)
      setRows(result.response.data)
      setPagination(result.response.pagination)
    } catch {
      toast.error('Failed to load purchase transactions.')
    } finally {
      setIsLoading(false)
    }
  }, [
    client,
    page,
    perPage,
    statusFilter,
    debouncedOrderId,
    debouncedRecurly,
    debouncedEmail,
    startDate,
    endDate,
    sortBy,
    sortDir,
  ])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFilterChange = setter => e => {
    setter(e.target.value)
    setPage(1)
  }

  const handleViewDetails = useCallback(
    async orderId => {
      setSelectedOrderId(orderId)
      setDetailSteps([])
      setDetailLoading(true)
      try {
        const result = await client(`purchase-txn/${orderId}`)
        setDetailSteps(result.response)
      } catch {
        toast.error('Failed to load transaction details.')
      } finally {
        setDetailLoading(false)
      }
    },
    [client],
  )

  const handleCloseDetail = () => {
    setSelectedOrderId(null)
    setDetailSteps([])
  }

  const clearFilters = () => {
    setStatusFilter('')
    setOrderIdFilter('')
    setRecurlyFilter('')
    setEmailFilter('')
    setStartDate(null)
    setEndDate(null)
    setSortBy('')
    setSortDir('desc')
    setPage(1)
  }

  return (
    <ContentArea style={{padding: '12px 16px'}}>
      <PageTitle>Purchase Links</PageTitle>
      {pagination && pagination.total > 0 && (
        <ResultCount>
          {pagination.total} result{pagination.total !== 1 ? 's' : ''}
        </ResultCount>
      )}

      {/* Table + Filters */}
      <MainArea style={{fontSize: 'clamp(11px, 1vw, 13px)'}}>
        <FiltersRow>
          {/* <FormField style={{flex: '0 0 130px', minWidth: '80px'}}>
            <FormLabel>Order ID</FormLabel>
            <FormInput
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="e.g. 12345"
              value={orderIdFilter}
              onChange={handleFilterChange(setOrderIdFilter)}
            />
          </FormField> */}

          <FormField style={{flex: '1 1 160px', minWidth: '140px'}}>
            <FormLabel>Recurly Account Code</FormLabel>
            <FormInput
              type="text"
              placeholder="Search..."
              value={recurlyFilter}
              onChange={handleFilterChange(setRecurlyFilter)}
            />
          </FormField>

          <FormField style={{flex: '1 1 160px', minWidth: '140px'}}>
            <FormLabel>Account Email</FormLabel>
            <FormInput
              type="text"
              placeholder="Search by email..."
              value={emailFilter}
              onChange={handleFilterChange(setEmailFilter)}
            />
          </FormField>

          <FormField style={{flex: '1 1 130px', minWidth: '110px'}}>
            <FormLabel>Status</FormLabel>
            <FormSelect
              value={statusFilter}
              onChange={handleFilterChange(setStatusFilter)}
            >
              <option value="">All</option>
              <option value="Failed">Failed</option>
              <option value="Completed">Completed</option>
              <option value="Submitted">Not Started</option>
            </FormSelect>
          </FormField>

          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={({startDate: s, endDate: e}) => {
              setStartDate(s)
              setEndDate(e)
              setPage(1)
            }}
            onClear={() => {
              setStartDate(null)
              setEndDate(null)
              setPage(1)
            }}
          />

          <div style={{display: 'flex', alignItems: 'flex-end'}}>
            <ClearBtn type="button" onClick={clearFilters}>
              Clear Filters
            </ClearBtn>
          </div>
        </FiltersRow>

        <TableWrapper>
          <StyledTable
            ref={tableRef}
            style={{
              tableLayout: 'fixed',
              width: colWidths.reduce((a, b) => a + b, 0),
            }}
          >
            <THead>
              <TR>
                {COLUMNS.map((col, i) => (
                  <TH
                    key={col.key}
                    onDoubleClick={
                      col.key !== 'actions' ? () => autoFitColumn(i) : undefined
                    }
                    style={{
                      width: colWidths[i],
                      position: 'relative',
                      userSelect: 'none',
                    }}
                  >
                    {col.key !== 'actions' ? (
                      <span
                        onClick={() => handleSort(col.key)}
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {col.label}
                        </span>
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: '10px',
                            opacity: sortBy === col.key ? 1 : 0.35,
                            color: sortBy === col.key ? '#0327a9' : 'inherit',
                          }}
                        >
                          {sortBy === col.key
                            ? sortDir === 'asc'
                              ? '↑'
                              : '↓'
                            : '⇅'}
                        </span>
                      </span>
                    ) : null}
                    {col.key !== 'actions' && (
                      <ResizeHandle onMouseDown={e => startResize(e, i)} />
                    )}
                  </TH>
                ))}
              </TR>
            </THead>
            <TBody>
              {isLoading ? (
                Array.from({length: 5}).map((_, i) => (
                  <TR key={`sk-${i}`}>
                    {COLUMNS.map(col => (
                      <TD key={col.key}>
                        <div
                          style={{
                            height: '14px',
                            borderRadius: '4px',
                            background:
                              'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'skeleton-shimmer 1.5s infinite',
                            width: col.key === 'actions' ? '60px' : '80%',
                          }}
                        />
                      </TD>
                    ))}
                  </TR>
                ))
              ) : rows.length === 0 ? (
                <TR>
                  <TD colSpan={COLUMNS.length}>
                    <EmptyState>
                      <EmptyStateTitle>
                        No purchase transactions found.
                      </EmptyStateTitle>
                    </EmptyState>
                  </TD>
                </TR>
              ) : (
                rows.map((row, idx) => (
                  <TR key={row.order_id || idx}>
                    <TD
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.recurly_account_code || ''}
                    >
                      {row.recurly_account_code}
                    </TD>
                    <TD
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.account_email || ''}
                    >
                      {row.account_email || '—'}
                    </TD>
                    <TD
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.account_name?.trim() || ''}
                    >
                      {row.account_name?.trim() || '—'}
                    </TD>
                    <TD>
                      <StatusPill
                        status={row.status}
                        clickable
                        onClick={() => handleViewDetails(row.order_id)}
                      >
                        {
                          (
                            STATUS_CONFIG[row.status] ||
                            STATUS_CONFIG.NotStarted
                          ).label
                        }
                      </StatusPill>
                    </TD>
                    <TD
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                      }}
                    >
                      {formatDate(row.created_at)}
                    </TD>
                    <TD>
                      <ActionBtn
                        onClick={() => handleViewDetails(row.order_id)}
                      >
                        <img src={PTView} width="13" alt="" />
                      </ActionBtn>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </StyledTable>
        </TableWrapper>

        {pagination && (
          <ProposalPagination
            currentPage={pagination.page}
            totalItems={pagination.total}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={value => {
              setPerPage(value)
              setPage(1)
            }}
          />
        )}
      </MainArea>

      {/* Detail Modal */}
      <DetailModal
        transactionId={selectedOrderId}
        steps={detailSteps}
        loading={detailLoading}
        onClose={handleCloseDetail}
      />

      <style>{`
        @keyframes skeleton-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </ContentArea>
  )
}
